import { useState } from 'react';
import { CheckCircle, Shield, Clock, CreditCard, ArrowRight, ArrowLeft, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MEMBERSHIPS } from '../data/mockData';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export const Memberships = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkoutStep, setCheckoutStep] = useState(0); // 0 = plans, 1 = datos, 2 = pago, 3 = confirmación
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cardNumber: '',
    expiry: '',
    cvv: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setCheckoutStep(1);
  };

  const handleSubmitStep1 = (e) => {
    e.preventDefault();
    setCheckoutStep(2);
  };

  const handleSubmitStep2 = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsProcessing(false);
    setCheckoutStep(3);
    toast.success('¡Pago exitoso!', {
      description: 'Tu membresía está activa.',
    });
  };

  const resetCheckout = () => {
    setSelectedPlan(null);
    setCheckoutStep(0);
    setFormData({
      name: '',
      email: '',
      phone: '',
      cardNumber: '',
      expiry: '',
      cvv: '',
    });
  };

  const CheckoutSteps = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {['Datos', 'Pago', 'Listo'].map((step, idx) => (
        <div key={step} className="flex items-center">
          <div className="checkout-step">
            <div className={`checkout-step-number ${
              checkoutStep > idx + 1 ? 'completed' : 
              checkoutStep === idx + 1 ? 'active' : 'pending'
            }`}>
              {checkoutStep > idx + 1 ? <CheckCircle className="w-4 h-4" /> : idx + 1}
            </div>
            <span className={`text-sm font-medium ${
              checkoutStep >= idx + 1 ? 'text-slate-900' : 'text-slate-400'
            }`}>
              {step}
            </span>
          </div>
          {idx < 2 && (
            <div className={`w-12 h-0.5 mx-2 ${
              checkoutStep > idx + 1 ? 'bg-slate-900' : 'bg-slate-200'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-24 md:pb-8 bg-slate-50" data-testid="memberships-page">
      <div className="container-app">
        {checkoutStep === 0 ? (
          <>
            {/* Header */}
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4 block">
                Membresías
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">
                Elige tu plan ideal
              </h1>
              <p className="text-lg text-slate-500 max-w-xl mx-auto">
                Entrena sin límites con acceso a todas nuestras clases y disciplinas.
              </p>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center justify-center gap-6 mb-12">
              <span className="trust-badge">
                <Shield className="w-4 h-4 text-emerald-500" />
                Cancelación sin penalidad
              </span>
              <span className="trust-badge">
                <Clock className="w-4 h-4 text-blue-500" />
                Pausa tu membresía
              </span>
              <span className="trust-badge">
                <CreditCard className="w-4 h-4 text-purple-500" />
                Pago seguro
              </span>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {MEMBERSHIPS.map((plan, index) => (
                <div
                  key={plan.id}
                  className={`membership-card ${plan.popular ? 'featured' : ''} bg-white animate-fade-in-up stagger-${index + 1}`}
                  data-testid={`membership-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-[#16263B] text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide">
                        Más popular
                      </span>
                    </div>
                  )}
                  
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">{plan.description}</p>
                  
                  <div className="flex items-baseline gap-1 mb-2">
                    {plan.originalPrice && (
                      <span className="text-lg text-slate-400 line-through mr-2">
                        ${plan.originalPrice.toLocaleString()}
                      </span>
                    )}
                    <span className="font-heading font-bold text-4xl text-slate-900">
                      ${plan.price.toLocaleString()}
                    </span>
                    <span className="text-slate-500">{plan.period}</span>
                  </div>
                  
                  {plan.savings && (
                    <p className="text-sm text-emerald-600 mb-4">
                      Ahorras ${plan.savings} al año
                    </p>
                  )}
                  
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-[#16263B] flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Button
                    onClick={() => handleSelectPlan(plan)}
                    className={`w-full ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    {plan.popular ? 'Comenzar ahora' : 'Seleccionar'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ))}
            </div>

            {/* FAQ / Trust */}
            <div className="mt-16 max-w-2xl mx-auto text-center">
              <h3 className="font-heading font-bold text-lg text-slate-900 mb-4">
                Preguntas frecuentes
              </h3>
              <div className="grid gap-4 text-left">
                {[
                  { q: '¿Puedo cancelar en cualquier momento?', a: 'Sí, puedes cancelar tu membresía mensual sin penalidad. La membresía anual tiene un compromiso de 12 meses.' },
                  { q: '¿Qué incluye la prueba gratis?', a: 'Una clase completa en la disciplina de tu elección, sin compromiso.' },
                  { q: '¿Puedo pausar mi membresía?', a: 'Sí, puedes pausar hasta 30 días al año sin costo adicional.' },
                ].map((faq, i) => (
                  <div key={i} className="bg-slate-50 rounded-xl p-4">
                    <p className="font-semibold text-slate-900 mb-1">{faq.q}</p>
                    <p className="text-sm text-slate-500">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* Checkout Flow */
          <Dialog open={checkoutStep > 0} onOpenChange={(open) => !open && resetCheckout()}>
            <DialogContent className="max-w-lg p-0 overflow-hidden">
              <div className="p-6">
                <DialogHeader className="mb-4">
                  <DialogTitle className="font-heading text-xl">
                    {checkoutStep === 3 ? '¡Bienvenido a Pulse!' : `Checkout - ${selectedPlan?.name}`}
                  </DialogTitle>
                </DialogHeader>

                {checkoutStep < 3 && <CheckoutSteps />}

                {/* Step 1: Personal Info */}
                {checkoutStep === 1 && (
                  <form onSubmit={handleSubmitStep1} className="space-y-4">
                    <div>
                      <Label htmlFor="checkout-name">Nombre completo</Label>
                      <Input
                        id="checkout-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="mt-1.5 h-12 bg-slate-50 border-transparent focus:border-slate-300 focus:bg-white rounded-xl"
                        data-testid="checkout-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="checkout-email">Email</Label>
                      <Input
                        id="checkout-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="mt-1.5 h-12 bg-slate-50 border-transparent focus:border-slate-300 focus:bg-white rounded-xl"
                        data-testid="checkout-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="checkout-phone">Teléfono / WhatsApp</Label>
                      <Input
                        id="checkout-phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="mt-1.5 h-12 bg-slate-50 border-transparent focus:border-slate-300 focus:bg-white rounded-xl"
                        data-testid="checkout-phone"
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={resetCheckout}
                        className="flex-1 h-12 rounded-full"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Volver
                      </Button>
                      <Button type="submit" className="flex-1 btn-primary h-12" data-testid="checkout-step1-next">
                        Continuar
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </form>
                )}

                {/* Step 2: Payment */}
                {checkoutStep === 2 && (
                  <form onSubmit={handleSubmitStep2} className="space-y-4">
                    {/* Order summary */}
                    <div className="bg-slate-50 rounded-xl p-4 mb-4">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-slate-900">{selectedPlan?.name}</span>
                        <span className="font-bold text-slate-900">
                          ${selectedPlan?.price.toLocaleString()}{selectedPlan?.period}
                        </span>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="card-number">Número de tarjeta</Label>
                      <Input
                        id="card-number"
                        placeholder="1234 5678 9012 3456"
                        value={formData.cardNumber}
                        onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value })}
                        required
                        className="mt-1.5 h-12 bg-slate-50 border-transparent focus:border-slate-300 focus:bg-white rounded-xl"
                        data-testid="checkout-card"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="expiry">Vencimiento</Label>
                        <Input
                          id="expiry"
                          placeholder="MM/AA"
                          value={formData.expiry}
                          onChange={(e) => setFormData({ ...formData, expiry: e.target.value })}
                          required
                          className="mt-1.5 h-12 bg-slate-50 border-transparent focus:border-slate-300 focus:bg-white rounded-xl"
                          data-testid="checkout-expiry"
                        />
                      </div>
                      <div>
                        <Label htmlFor="cvv">CVV</Label>
                        <Input
                          id="cvv"
                          placeholder="123"
                          value={formData.cvv}
                          onChange={(e) => setFormData({ ...formData, cvv: e.target.value })}
                          required
                          className="mt-1.5 h-12 bg-slate-50 border-transparent focus:border-slate-300 focus:bg-white rounded-xl"
                          data-testid="checkout-cvv"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                      <Shield className="w-4 h-4" />
                      Pago seguro encriptado
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCheckoutStep(1)}
                        className="flex-1 h-12 rounded-full"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Volver
                      </Button>
                      <Button 
                        type="submit" 
                        className="flex-1 btn-primary h-12"
                        disabled={isProcessing}
                        data-testid="checkout-pay-btn"
                      >
                        {isProcessing ? (
                          <span className="flex items-center gap-2">
                            <span className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                            Procesando...
                          </span>
                        ) : (
                          <>Pagar ${selectedPlan?.price.toLocaleString()}</>
                        )}
                      </Button>
                    </div>
                  </form>
                )}

                {/* Step 3: Success */}
                {checkoutStep === 3 && (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 bg-[#16263B] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                      <CheckCircle className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">
                      ¡Membresía activada!
                    </h3>
                    <p className="text-slate-500 mb-6">
                      Ya puedes reservar todas las clases que quieras.
                    </p>
                    <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                      <p className="text-sm text-slate-500 mb-1">Tu plan</p>
                      <p className="font-semibold text-slate-900">{selectedPlan?.name}</p>
                      <p className="text-sm text-slate-500 mt-2 mb-1">Email de confirmación</p>
                      <p className="font-semibold text-slate-900">{formData.email}</p>
                    </div>
                    <Button
                      onClick={resetCheckout}
                      className="w-full btn-primary h-12"
                      data-testid="checkout-done-btn"
                    >
                      Explorar clases
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};
